import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

interface CookieInfo {
  name: string;
  type: 'tecnico' | 'analitico';
  duration: string;
  purpose: string;
}

const cookies: CookieInfo[] = [
  {
    name: 'rc-auth',
    type: 'tecnico',
    duration: 'Sessione / 30 giorni',
    purpose: 'Mantiene lo stato di autenticazione dell\'utente (JWT token).',
  },
  {
    name: 'rc-theme',
    type: 'tecnico',
    duration: 'Persistente',
    purpose: 'Salva la preferenza tema (dark/light/system).',
  },
  {
    name: 'rc-cookie-consent',
    type: 'tecnico',
    duration: 'Persistente',
    purpose: 'Registra la scelta dell\'utente riguardo al consenso cookie.',
  },
];

export default function CookiePolicyPage() {
  return (
    <>
      <Helmet>
        <title>Cookie Policy — React-City</title>
        <meta name="description" content="Informativa sui cookie utilizzati da React-City — Movimento del Dono." />
      </Helmet>

      <div className="min-h-screen bg-white dark:bg-[#1b1b1f] transition-colors">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          {/* Breadcrumb */}
          <nav className="mb-8 text-sm text-muted">
            <Link to="/" className="hover:text-primary-500">Home</Link>
            <span className="mx-2">›</span>
            <span className="text-heading">Cookie Policy</span>
          </nav>

          <h1 className="text-3xl md:text-4xl font-bold text-heading mb-2">
            Cookie Policy
          </h1>
          <p className="text-muted mb-10">
            Ultimo aggiornamento: {new Date().toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' })}
          </p>

          <div className="space-y-8">
            {/* Intro */}
            <section>
              <h2 className="text-xl font-semibold text-heading mb-3">Cosa sono i cookie</h2>
              <p className="text-sm text-muted">
                I cookie sono piccoli file di testo che i siti web salvano sul tuo dispositivo
                durante la navigazione. Vengono utilizzati per far funzionare il sito in modo
                efficiente e per fornire informazioni ai proprietari del sito.
              </p>
            </section>

            {/* Tipologie */}
            <section>
              <h2 className="text-xl font-semibold text-heading mb-3">Tipologie di cookie utilizzati</h2>
              <div className="grid gap-4">
                <div className="card-flat">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="badge-green">Tecnici</span>
                    <span className="text-xs text-muted">Strettamente necessari</span>
                  </div>
                  <p className="text-sm text-muted">
                    Sono essenziali per il funzionamento del sito. Senza di essi, il sito non
                    potrebbe fornire i servizi richiesti (autenticazione, preferenze tema, consenso).
                    Non richiedono il consenso dell'utente.
                  </p>
                </div>

                <div className="card-flat">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="badge-blue">Analitici</span>
                    <span className="text-xs text-muted">Previo consenso</span>
                  </div>
                  <p className="text-sm text-muted">
                    Permettono di raccogliere dati aggregati e anonimi sul modo in cui i visitatori
                    utilizzano il sito. Attualmente <strong className="text-heading">non utilizziamo cookie analitici</strong>.
                    In caso di attivazione futura, verrà richiesto il consenso esplicito.
                  </p>
                </div>
              </div>
            </section>

            {/* Tabella */}
            <section>
              <h2 className="text-xl font-semibold text-heading mb-3">Cookie utilizzati</h2>
              <div className="overflow-x-auto rounded-xl border border-surface-200 dark:border-surface-800">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-surface-50 dark:bg-surface-800/50">
                      <th className="table-header px-4 py-3">Nome</th>
                      <th className="table-header px-4 py-3">Tipo</th>
                      <th className="table-header px-4 py-3">Durata</th>
                      <th className="table-header px-4 py-3">Finalità</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cookies.map((c) => (
                      <tr key={c.name} className="table-row">
                        <td className="px-4 py-3 font-mono text-xs text-heading">{c.name}</td>
                        <td className="px-4 py-3">
                          <span className={c.type === 'tecnico' ? 'badge-green' : 'badge-blue'}>
                            {c.type}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-muted text-xs">{c.duration}</td>
                        <td className="px-4 py-3 text-muted text-xs">{c.purpose}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="mt-3 text-xs text-muted">
                Nota: i cookie indicati come "tecnici" utilizzano il <code className="text-primary-500">localStorage</code> del browser
                e non vengono inviati al server con ogni richiesta HTTP.
              </p>
            </section>

            {/* Gestione */}
            <section>
              <h2 className="text-xl font-semibold text-heading mb-3">Come gestire i cookie</h2>
              <p className="text-sm text-muted mb-3">
                Puoi gestire le preferenze dei cookie in qualsiasi momento:
              </p>
              <ul className="space-y-2 text-sm text-muted">
                <li className="flex items-start gap-2">
                  <span className="text-primary-500 mt-0.5">•</span>
                  <span><strong className="text-heading">Banner cookie:</strong> utilizza il banner che appare alla prima visita per accettare o rifiutare.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary-500 mt-0.5">•</span>
                  <span><strong className="text-heading">Impostazioni browser:</strong> puoi configurare il tuo browser per bloccare o eliminare i cookie.
                    Consulta la guida del tuo browser per le istruzioni specifiche.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary-500 mt-0.5">•</span>
                  <span><strong className="text-heading">Reset consenso:</strong> puoi cancellare il localStorage del sito per reimpostare tutte le preferenze.</span>
                </li>
              </ul>
            </section>

            {/* Contatti */}
            <section>
              <h2 className="text-xl font-semibold text-heading mb-3">Contatti</h2>
              <p className="text-sm text-muted">
                Per domande sulla Cookie Policy, scrivi a{' '}
                <a href="mailto:privacy@react-city.it" className="text-primary-500 underline">privacy@react-city.it</a>.
                Per la protezione dei tuoi dati personali, consulta la nostra{' '}
                <Link to="/privacy" className="text-primary-500 underline">Privacy Policy</Link>.
              </p>
            </section>
          </div>

          {/* Back */}
          <div className="mt-12 pt-8 border-t divider">
            <Link to="/" className="btn-secondary">
              ← Torna alla home
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}
